import Requester from '@cobliteam/api-lib/src/request/Requester';
import Environment from '@cobliteam/react-environments';
import { DefaultChatTransport } from 'ai';
import type { AgentNames, Component, ResourceId, Source } from '../types';

const environment = Environment.getInstance().getEnvironment();
const MASTRA_API_URL = environment.mastraApi;
const requester = new Requester({ host: environment.herbieApiHost });

export type ChatScope = 'full' | 'recommendations';

export type ChatTransportProps = {
  agentName: AgentNames;
  threadId?: string;
  resourceId: ResourceId;
  source: Source;
  component: Component;
  url: string;
  /**
   * Restricts the tool pool the agent can use. 'recommendations' exposes only
   * the recommendation-action tools (+ their context reads). Defaults to 'full'.
   * Backend re-validates against the user's `ai-chat` flag — frontend value is
   * a UX hint, not a security boundary.
   */
  scope?: ChatScope;
  extraBody?: Record<string, unknown>;
};

export const chatTransport = ({ agentName, threadId, resourceId, source, component, url, scope, extraBody }: ChatTransportProps) => {
  if (!threadId) {
    throw new Error('Thread ID is required');
  }

  if (!resourceId) {
    throw new Error('Resource ID is required');
  }

  if (!agentName) {
    throw new Error('Agent name is required');
  }

  return new DefaultChatTransport({
    api: `${MASTRA_API_URL}/api/v1/chat/${agentName}`,
    prepareSendMessagesRequest: (options) => {
      return {
        body: {
          ...options,
          ...(extraBody ?? {}),
          threadId,
          resourceId,
          source,
          component,
          url,
          ...(scope ? { scope } : {}),
          memory: {
            thread: threadId,
            resource: resourceId,
          },
        },
      };
    },
    headers: async () => {
      const accessToken = await requester.getValidToken();
      return { Authorization: `Bearer ${accessToken}` };
    },
  });
};
